import { Row } from "reactstrap";
import { v4 as uuidv4 } from "uuid";
import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { GridRowModes } from "@mui/x-data-grid";

import { ReactComponent as DeleteIcon } from "assets/img/svg/Delete.svg";
import { ReactComponent as EditIcon } from "assets/img/svg/Edit2.svg";
import { ReactComponent as CrossIcon } from "assets/img/svg/CrossIcon.svg";

import CustomButton from "components/CustomButton";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import { LastUpdatedDetails } from "views/pages/manage/edpOps/stockManagement/materialDistribution/helper";
import { fetchAllPromisedData } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { endpoint } from "views/pages/manage/common/constant";

const COMPONENT_OPTIONS = [
  { label: "AIATS", value: "AIATS" },
  { label: "Tablet", value: "Tablet" },
];

const MapComponents = ({
  additionalComponentMappingList = [],
  setAdditionalComponentMappingList,
  isActionVisible = true,
  isDisabled = true,
  ...props
}) => {
  const masterDropdowns = useSelector(
    (state) => state.globalData?.masterDropdowns,
  );

  const [courseOptions, setCourseOptions] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowForm, setRowForm] = useState({});
  const [editRowId, setEditRowId] = useState(null);

  useEffect(() => {
    fecthCourses();
  }, []);

  const fecthCourses = async () => {
    const response = await fetchAllPromisedData(endpoint.course_details.getAll);

    const courses = response?.map((obj) => ({
      ...obj,
      label: `${obj.courseId} - ${obj.courseName}`,
      value: obj.courseId,
    }));
    setCourseOptions(courses || []);
  };

  // 🔢 Add Row
  const handleAddRow = () => {
    if (editRowId) return;

    const id = uuidv4();

    const newRow = {
      id,
      component: "",
      courseId: "",
      courseLabel: "",
      courseDescription: "",
      academicCareerId: "",
      academicCareerDispValue: "",
      academicGroupId: "",
      academicGroupDispValue: "",
      isNew: true,
    };

    setAdditionalComponentMappingList((prev) => [...prev, newRow]);

    setEditRowId(id);
    setRowForm(newRow);

    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  // ✏️ Edit
  const handleEditClick = (row) => {
    if (editRowId) return;

    setEditRowId(row.id);
    setRowForm(row);

    setRowModesModel((prev) => ({
      ...prev,
      [row.id]: { mode: GridRowModes.Edit, fieldToFocus: "component" },
    }));
  };

  // 💾 Save
  const handleSaveClick = (id) => {
    if (!rowForm.component || !rowForm.courseId) return;

    const updatedList = additionalComponentMappingList.map((r) =>
      r.id === id ? { ...rowForm, isNew: false } : r,
    );

    setAdditionalComponentMappingList(updatedList);

    setEditRowId(null);
    setRowForm({});

    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  // ❌ Cancel
  const handleCancelClick = (id) => {
    const isNewRow = additionalComponentMappingList.find(
      (r) => r.id === id,
    )?.isNew;

    if (isNewRow) {
      setAdditionalComponentMappingList((prev) =>
        prev.filter((r) => r.id !== id),
      );
    }

    setEditRowId(null);
    setRowForm({});

    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  // 🗑 Delete
  const handleDeleteClick = (id) => {
    setAdditionalComponentMappingList((prev) =>
      prev.filter((r) => r.id !== id),
    );
  };

  // 🔄 Field Change
  const handleFieldChange = (key, value) => {
    let obj = {};

    if (key === "component") {
      obj.component = value;
    } else if (key === "courseId") {
      obj.courseId = value.value;
      obj.courseLabel = value.courseName;
      obj.courseDescription = value.courseDescription;
      obj.academicCareerId = value.academicCareerId;
      obj.academicCareerDispValue = masterDropdowns.academicCareer.find(
        (obj) => obj.value === value.academicCareerId,
      ).label;
      obj.academicGroupId = value.academicGroupId;
      obj.academicGroupDispValue = masterDropdowns.stream.find(
        (obj) => obj.value === value.academicCareerId,
      ).label;
    }

    setRowForm((prev) => ({ ...prev, ...obj }));
  };

  // 📊 Columns
  const columns = [
    new AGridColDef("serial", "S. No.").renderCellComponent(
      (params) =>
        additionalComponentMappingList.findIndex(
          (r) => r.id === params.row.id,
        ) + 1,
    ),

    // Component
    new AGridColDef("component", "Component")
      .setMinWidth(150)
      .editable(true)
      .renderCellComponent((p) => {
        return p.value ?? "-";
      })
      .renderEditCellComponent(() => (
        <div className="w-100">
          <AAutoComplete
            items={COMPONENT_OPTIONS}
            currentValue={
              COMPONENT_OPTIONS.find((c) => c.value === rowForm.component) ||
              null
            }
            handleChange={(val) =>
              handleFieldChange("component", val?.value || "")
            }
            isMulti={false}
          />
        </div>
      )),

    // Course ID
    new AGridColDef("courseId", "Course ID")
      .setMinWidth(160)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <div className="w-100">
          <AAutoComplete
            items={courseOptions}
            currentValue={
              courseOptions.find((c) => c.value == rowForm.courseId) || null
            }
            handleChange={(val) => handleFieldChange("courseId", val || {})}
            isMulti={false}
          />
        </div>
      )),

    // Description
    new AGridColDef("courseDescription", "Description")
      .setMinWidth(250)
      .editable(true)
      .renderCellComponent((p) => {
        const desc = p.value ?? "-";
        return (
          <Tooltip title={desc}>
            <span className="ellipsis-text">
              {desc.length > 20 ? `${desc.slice(0, 20)}...` : desc}
            </span>
          </Tooltip>
        );
      })
      .renderEditCellComponent(() => (
        <Tooltip title={rowForm.courseDescription}>
          <div>{rowForm?.courseDescription || "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("academicCareerDispValue", "Career")
      .setMinWidth(50)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <Tooltip title={rowForm.academicCareerDispValue}>
          <div>{rowForm?.academicCareerDispValue || "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("academicGroupDispValue", "Stream")
      .setMinWidth(50)
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <Tooltip title={rowForm.academicGroupDispValue}>
          <div>{rowForm?.academicGroupDispValue || "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("updatedOn", "Updated On")
      .setMinWidth(180)
      .renderCellComponent((p) =>
        p.value ? LastUpdatedDetails(p.value) : "-",
      ),

    new AGridColDef("updatedBy", "Updated By").renderCellComponent((p) =>
      p.value ? p.value : "-",
    ),
  ];

  if (isActionVisible)
    // Actions
    columns.push(
      new AGridColDef("actions", "Actions")
        .setMinWidth(150)
        .renderCellComponent((params) => {
          const isEditing = editRowId === params.row.id;

          if (isEditing) {
            return (
              <>
                <AButton
                  size="xs"
                  onClick={() => handleSaveClick(params.row.id)}
                  disabled={!rowForm.component || !rowForm.courseId}
                  variant="primary_filled"
                >
                  Save
                </AButton>
                <IconButtonWrapper
                  onClick={() => handleCancelClick(params.row.id)}
                >
                  <CrossIcon />
                </IconButtonWrapper>
              </>
            );
          }

          return (
            <>
              <IconButtonWrapper onClick={() => handleEditClick(params.row)}>
                <EditIcon />
              </IconButtonWrapper>
              <IconButtonWrapper
                onClick={() => handleDeleteClick(params.row.id)}
              >
                <DeleteIcon />
              </IconButtonWrapper>
            </>
          );
        }),
    );

  // ✅ Last Updated (API only)
  const lastUpdatedRow = [...additionalComponentMappingList]
    .filter((r) => r.updatedOn)
    .sort((a, b) => new Date(b.updatedOn) - new Date(a.updatedOn))[0];

  return (
    <>
      <div className="d-flex justify-content-between mb-2">
        {isActionVisible && (
          <AButton
            onClick={handleAddRow}
            variant="outline"
            size="xs"
            disabled={!!editRowId}
          >
            + Add Mapping
          </AButton>
        )}
      </div>

      <ADataGrid
        rows={additionalComponentMappingList}
        columns={columns}
        rowId={(row) => row.id}
        height="350px"
        rowModesModel={rowModesModel}
      />

      <div className="d-flex justify-content-between mt-2 text-xs text-muted">
        <div>
          {lastUpdatedRow
            ? `Last Updated on  ${LastUpdatedDetails(lastUpdatedRow.updatedOn, lastUpdatedRow.updatedBy)}`
            : "No updates yet"}
        </div>

        <div>{additionalComponentMappingList.length} Mapping(s)</div>
      </div>

      {!isDisabled && isActionVisible && (
        <Row className="justify-content-end pl-4 pr-4 pb-4 mt-4">
          <CustomButton
            type="normal"
            color="info"
            content={
              props?.fetchingIsBreakupValid ? (
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                "Preview"
              )
            }
            permissionType={"C,U"}
            permissionSet={props?.userPermissions}
            onClick={() => props?.feeBreakupHandler()}
            disabled={props.fetchingIsBreakupValid || !props?.classArray.length}
          />
        </Row>
      )}
    </>
  );
};

export default MapComponents;
